import WhatIfInventoryAnalysis from './WhatIfInventoryAnalysis';
import { featureFlags } from '@/utils/featureFlags';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import InventoryDataTable from "@/components/data-table/InventoryDataTable";
import { useProgressiveInventory } from "@/hooks/useProgressiveInventory";
import { useTimezoneInfo } from "@/hooks/useTimezoneInfo";
import { UnifiedExportManager } from './UnifiedExportManager';
import { format, subMonths } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import MultiColumnFilter from '@/components/data-table/MultiColumnFilter';
import { FilterOperation } from '@/hooks/useConnectionData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InventoryTimeSeriesView from "./InventoryTimeSeriesView";
import ManualAdjustmentForm from "./inventory/ManualAdjustmentForm";
import InventoryAuditLogTable from "./inventory/InventoryAuditLogTable";
import ImportTypeDialog, { type ImportType } from "./inventory/ImportTypeDialog";
import ReceivedInventoryCsvUpload from "./inventory/ReceivedInventoryCsvUpload";
import SnapshotCsvUpload from "./inventory/SnapshotCsvUpload";
import { Lock, Plus, Archive, Settings, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import InventorySourceSettings from '@/components/admin/InventorySourceSettings';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInventoryLedgerSources } from "@/hooks/useInventoryLedgerSources";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  connectionHasAmazonInventoryCsv,
  connectionHasShopifyInventoryCsv,
  isCsvLikeConnection,
} from "@/shared/inventoryCsvConnectionChannels";

interface OrganisationInventoryTabProps {
  organisation: any;
  onOrganisationUpdate?: () => void;
}

const OrganisationInventoryTab: React.FC<OrganisationInventoryTabProps> = ({
  organisation,
  onOrganisationUpdate,
}) => {
  const isTestClone = !!organisation?.is_test_clone;
  const reportType = 'stock-levels'; // Default to stock levels
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [customStartDate, setCustomStartDate] = useState<Date>(subMonths(new Date(), 1));
  const [customEndDate, setCustomEndDate] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [showExportManager, setShowExportManager] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Array<{ column: string; value: string; operation: FilterOperation }>>([]);
  const [activeTab, setActiveTab] = useState<'table' | 'trends' | 'financing' | 'whatif' | 'adjust' | 'audit'>('table');
  const [trendsViewMode, setTrendsViewMode] = useState<'table' | 'graph'>('table');
  const [sortColumn, setSortColumn] = useState<string | null>('Held');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [snapshotsEnabled, setSnapshotsEnabled] = useState(false);
  const [loadingSnapshotStatus, setLoadingSnapshotStatus] = useState(true);
  const [enablingSnapshots, setEnablingSnapshots] = useState(false);
  const [financingTablesCreated, setFinancingTablesCreated] = useState(false);
  const [creatingFinancingTable, setCreatingFinancingTable] = useState(false);
  // Financing Tapes state
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [lockDate, setLockDate] = useState("");
  const [tapeName, setTapeName] = useState("");
  const [notes, setNotes] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [fundingCycleDay, setFundingCycleDay] = useState<number | undefined>();

  const dateRange = 'custom'

  // SKU filter state for trends view
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [skuSearch, setSkuSearch] = useState<string>('');
  const [availableSkus, setAvailableSkus] = useState<string[]>([]);
  const [skuOffset, setSkuOffset] = useState(0);
  const [hasMoreSkus, setHasMoreSkus] = useState(true);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [skuLimitWarning, setSkuLimitWarning] = useState(false);
  const [exportingTapeId, setExportingTapeId] = useState<string | null>(null);
  const SKU_PAGE_SIZE = 10;
  const MAX_SKUS_FOR_GRAPH = 10;

  // Import flow state
  const [showImportTypeDialog, setShowImportTypeDialog] = useState(false);
  const [showReceivedUpload, setShowReceivedUpload] = useState(false);
  const [showSnapshotUpload, setShowSnapshotUpload] = useState(false);

  const { data: ledgerRegistrySources = [] } = useInventoryLedgerSources(
    organisation?.id,
    activeTab === "adjust" && Boolean(organisation?.id),
  );

  const adjustTabLedgerSummary = React.useMemo(() => {
    const live = ledgerRegistrySources.filter((s) => s.origin_kind === "live");
    const nonLive = ledgerRegistrySources.filter((s) => s.origin_kind !== "live");
    return {
      hybrid: live.length > 0 && nonLive.length > 0,
      liveOnly: live.length > 0 && nonLive.length === 0,
    };
  }, [ledgerRegistrySources]);

  // Get connection IDs for timezone info
  const connectionIds = React.useMemo(() => {
    if (!organisation?.connections) return [];
    return Object.values(organisation.connections)
      .filter((conn: any) => conn.service !== 'file' && conn.type !== 'file' && !conn.id?.startsWith('file-import-'))
      .map((conn: any) => conn.connectionId || conn.id)
      .filter(Boolean);
  }, [organisation]);

  const getDataConnections = () => {
    if (!organisation?.connections) return {};

    const dataConnections: any = {};
    Object.entries(organisation.connections).forEach(([key, value]: [string, any]) => {
      if (value.service !== 'file' && value.type !== 'file' && !value.id?.startsWith('file-import-')) {
        dataConnections[key] = value;
      }
    });

    return dataConnections;
  };

  const dataConnections = getDataConnections();
  const hasConnections = Object.keys(dataConnections).length > 0;
  const hasSnapshotData = organisation?.snapshot_table_created === true;
  const hasInventoryData = hasConnections || hasSnapshotData;

  // Get available sources from connections with proper labels for CSV imports
  const availableSources = React.useMemo(() => {
    if (!organisation?.connections) return [];
    const connections = Object.values(organisation.connections) as any[];
    const sources: string[] = [];

    // Check for Shopify connections
    const hasShopify = connections.some((conn: any) => {
      const type = conn.type?.toLowerCase() || '';
      const service = conn.service?.toLowerCase() || '';
      return type.includes('shopify') && service !== 'csv' && type !== 'csv';
    });

    const hasShopifyCsv = connections.some((conn: any) => {
      if (connectionHasShopifyInventoryCsv(conn)) return true;
      if (!isCsvLikeConnection(conn)) return false;
      const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
      return rawSub === 'shopify' && !connectionHasAmazonInventoryCsv(conn);
    });

    // Check for Amazon connections
    const hasAmazon = connections.some((conn: any) => {
      const type = conn.type?.toLowerCase() || '';
      const service = conn.service?.toLowerCase() || '';
      return type.includes('amazon') && service !== 'csv' && type !== 'csv';
    });

    const hasAmazonCsv = connections.some((conn: any) => {
      if (connectionHasAmazonInventoryCsv(conn)) return true;
      if (!isCsvLikeConnection(conn)) return false;
      const rawSub = String(conn.ecommerce_subtype || '').toLowerCase();
      return rawSub === 'amazon' && !connectionHasShopifyInventoryCsv(conn);
    });

    if (hasShopify) sources.push('Shopify');
    if (hasShopifyCsv) sources.push('Shopify CSV Import');
    if (hasAmazon) sources.push('Amazon');
    if (hasAmazonCsv) sources.push('Amazon CSV Import');

    return sources;
  }, [organisation]);

  // Get primary source from org config
  const primarySourceConfig = React.useMemo(() => {
    const config = organisation?.inventory_source_config;
    if (!config?.primary_source || config.primary_source === 'all') return null;
    return config.primary_source;
  }, [organisation?.inventory_source_config]);

  // Track previous config to detect changes
  const prevPrimarySourceRef = useRef<string | null>(primarySourceConfig);
  const prevAvailableSourcesRef = useRef<string[]>(availableSources);

  // Initialize selected sources from org config or all available, and react to config changes
  useEffect(() => {
    const prevAvailable = prevAvailableSourcesRef.current;
    const hadAllPreviously = prevAvailable.length > 0 && selectedSources.length === prevAvailable.length &&
      prevAvailable.every((s) => selectedSources.includes(s));
    const addedSources = availableSources.filter((s) => !prevAvailable.includes(s));

    // Detect if config actually changed (not initial load)
    const configChanged = prevPrimarySourceRef.current !== primarySourceConfig;

    if (configChanged) {
      prevPrimarySourceRef.current = primarySourceConfig;

      // Config changed - update selected sources
      if (availableSources.length > 0) {
        if (primarySourceConfig) {
          const matchingSources = availableSources.filter(source => {
            const lowerSource = source.toLowerCase();
            if (primarySourceConfig === 'shopify') return lowerSource === 'shopify';
            if (primarySourceConfig === 'amazon') return lowerSource === 'amazon';
            if (primarySourceConfig === 'shopify_csv') return lowerSource.includes('shopify') && lowerSource.includes('csv');
            if (primarySourceConfig === 'amazon_csv') return lowerSource.includes('amazon') && lowerSource.includes('csv');
            return false;
          });
          setSelectedSources(matchingSources.length > 0 ? matchingSources : availableSources);
        } else {
          setSelectedSources(availableSources);
        }
      }
    }
    // Initial load - set sources if empty
    else if (availableSources.length > 0 && selectedSources.length === 0) {
      if (primarySourceConfig) {
        // Filter to match the primary source config
        // Config values: 'shopify', 'amazon', 'shopify_csv', 'amazon_csv'
        const matchingSources = availableSources.filter(source => {
          const lowerSource = source.toLowerCase();
          if (primarySourceConfig === 'shopify') return lowerSource === 'shopify';
          if (primarySourceConfig === 'amazon') return lowerSource === 'amazon';
          if (primarySourceConfig === 'shopify_csv') return lowerSource.includes('shopify') && lowerSource.includes('csv');
          if (primarySourceConfig === 'amazon_csv') return lowerSource.includes('amazon') && lowerSource.includes('csv');
          return false;
        });
        setSelectedSources(matchingSources.length > 0 ? matchingSources : availableSources);
      } else {
        setSelectedSources(availableSources);
      }
    }
    // If user had "All Sources" selected and new sources appear (e.g. after upload),
    // keep selection aligned with "all" by auto-including the new sources.
    else if (!configChanged && hadAllPreviously && addedSources.length > 0) {
      setSelectedSources(availableSources);
    }

    prevAvailableSourcesRef.current = availableSources;
  }, [availableSources, selectedSources, primarySourceConfig]);

  // Fetch snapshot status
  useEffect(() => {
    const fetchSnapshotStatus = async () => {
      if (!organisation?.id) return;

      const client = supabase as any;
      const { data } = await client
        .from('organisations')
        .select('inventory_snapshots_enabled, snapshot_table_created, financing_tapes_table_created')
        .eq('id', organisation.id)
        .single();

      setSnapshotsEnabled(data?.inventory_snapshots_enabled && data?.snapshot_table_created);
      setFinancingTablesCreated(data?.financing_tapes_table_created || false);
      setLoadingSnapshotStatus(false);
    };

    fetchSnapshotStatus();
  }, [organisation?.id]);

  // Create financing tapes table if needed
  const createFinancingTapesTable = useCallback(async () => {
    if (!organisation?.id || financingTablesCreated || creatingFinancingTable) return;

    setCreatingFinancingTable(true);
    try {
      const { data, error } = await invokeFunction(supabase, 'create-financing-tapes-table', {
        body: { organisationId: organisation.id }
      });

      if (error) throw error;

      if (data?.success) {
        setFinancingTablesCreated(true);
        toast.success("Financing tapes table created successfully");
      }
    } catch (error: any) {
      console.error('Error creating financing tapes table:', error);
      toast.error(error.message || "Failed to create financing tapes table");
    } finally {
      setCreatingFinancingTable(false);
    }
  }, [organisation?.id, financingTablesCreated, creatingFinancingTable]);

  // Fetch financing tapes from snapshots database
  const { data: tapes, isLoading: isLoadingTapes } = useQuery({
    queryKey: ['financing-tapes', organisation?.id],
    queryFn: async () => {
      if (!financingTablesCreated) return [];

      const { data, error } = await invokeFunction(supabase, 'get-financing-tapes', {
        body: {
          organisationId: organisation?.id,
          status: 'active' // Only fetch active tapes by default
        }
      });

      if (error) throw error;
      return data?.tapes || [];
    },
    enabled: !!organisation?.id && activeTab === 'financing' && financingTablesCreated,
  });

  // Lock financing tape mutation
  const lockTapeMutation = useMutation({
    mutationFn: async (params: any) => {
      const { data, error } = await invokeFunction(supabase, 'lock-financing-tape', {
        body: params
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Financing tape locked successfully");
      queryClient.invalidateQueries({ queryKey: ['financing-tapes'] });
      setIsDialogOpen(false);
      resetFinancingForm();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to lock financing tape");
    },
  });

  // Archive tape mutation - updates status in snapshots DB
  const archiveTapeMutation = useMutation({
    mutationFn: async (tapeId: string) => {
      const { data, error } = await invokeFunction(supabase, 'update-financing-tape-status', {
        body: {
          organisationId: organisation?.id,
          tapeId,
          status: 'archived'
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Financing tape archived");
      queryClient.invalidateQueries({ queryKey: ['financing-tapes'] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to archive tape");
    },
  });

  // Export tape handler
  const handleExportTape = async (tapeId: string, tapeName: string) => {
    setExportingTapeId(tapeId);
    try {
      console.log(`[UI] Exporting tape: ${tapeId} for org: ${organisation?.id}`);

      const { data, error } = await invokeFunction(supabase, 'export-financing-tape', {
        body: {
          tapeId,
          organisationId: organisation?.id
        }
      });

      if (error) {
        console.error('[UI] Export error from edge function:', error);
        throw error;
      }

      // Check if response is an error JSON
      if (typeof data === 'object' && data?.error) {
        const errorMsg = data.details || data.error || 'Failed to export financing tape';
        console.error('[UI] Export returned error object:', data);
        throw new Error(errorMsg);
      }

      // Create blob and download
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financing_tape_${tapeName.replace(/[^a-z0-9]/gi, '_')}_${tapeId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`[UI] Export successful for tape: ${tapeId}`);
      toast.success("Export successful");
    } catch (error: any) {
      console.error('[UI] Export error:', error);
      const errorMessage = error?.message || 'Failed to export financing tape';
      toast.error(errorMessage);
    } finally {
      setExportingTapeId(null);
    }
  };

  const resetFinancingForm = () => {
    setLockDate("");
    setTapeName("");
    setNotes("");
    setIsRecurring(false);
    setFundingCycleDay(undefined);
  };

  const handleLockTape = () => {
    if (!lockDate) {
      toast.error("Please select a lock date");
      return;
    }

    lockTapeMutation.mutate({
      organisationId: organisation.id,
      lockDate,
      tapeName: tapeName || `Financing Tape - ${lockDate}`,
      notes,
      isRecurring,
      fundingCycleDay: isRecurring ? fundingCycleDay : null,
    });
  };

  const { getAllTimezones } = useTimezoneInfo(connectionIds);
  // Get timezone info - show "Multiple Timezones" badge if multiple sources selected
  const getTimezoneDisplay = () => {
    if (selectedSources.length === 0) return 'UTC';
    if (selectedSources.length > 1) return null; // Will show "Multiple Timezones" badge

    const timezones = getAllTimezones();
    return timezones[0]?.detected_timezone || 'UTC';
  };

  const timezoneLabel = getTimezoneDisplay();

  // Convert activeFilters to columnFilters format for backend
  const getColumnFilters = useCallback(() => {
    return activeFilters.map(f => ({
      column: f.column,
      operation: f.operation,
      value: f.value
    }));
  }, [activeFilters]);

  const {
    data,
    fullData, // For graph view - all data without pagination
    isLoading,
    error,
    totalCount,
    platforms,
    refetch
  } = useProgressiveInventory({
    organisation,
    reportType,
    sources: selectedSources,
    dateRange,
    customStartDate,
    customEndDate,
    pageSize: rowsPerPage,
    viewMode: activeTab === 'trends' ? 'timeseries' : 'snapshot',
    selectedSkus: activeTab === 'trends' ? selectedSkus : undefined,
    page: currentPage,
    onPageChange: setCurrentPage,
    sortColumn,
    sortDirection,
    graphViewActive: activeTab === 'trends' && trendsViewMode === 'graph',
    columnFilters: getColumnFilters()
  });

  const handleInventoryUploadSuccess = useCallback(() => {
    refetch();
    onOrganisationUpdate?.();
    queryClient.invalidateQueries({ queryKey: ["adjustment-deltas", organisation?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory-audit-log", organisation?.id] });
  }, [organisation?.id, onOrganisationUpdate, queryClient, refetch]);

  // Fetch available SKUs from the database
  const fetchAvailableSkus = useCallback(async (search: string = '', reset: boolean = false) => {
    if (loadingSkus || !organisation?.id) return;

    setLoadingSkus(true);
    const newOffset = reset ? 0 : skuOffset;

    try {
      const { data: response, error } = await invokeFunction(supabase, 'fetch-organisation-skus', {
        body: {
          organisationId: organisation.id,
          search,
          limit: SKU_PAGE_SIZE,
          offset: newOffset,
          sources: selectedSources
        }
      });

      if (error) throw error;

      if (reset) {
        setAvailableSkus(response.skus || []);
      } else {
        setAvailableSkus(prev => [...prev, ...(response.skus || [])]);
      }

      setHasMoreSkus(response.hasMore);
      setSkuOffset(newOffset + SKU_PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching SKUs:', error);
    } finally {
      setLoadingSkus(false);
    }
  }, [organisation?.id, selectedSources, skuOffset, loadingSkus]);

  // Load initial SKUs when trends tab is activated
  useEffect(() => {
    if (activeTab === 'trends' && availableSkus.length === 0) {
      setSkuOffset(0);
      setHasMoreSkus(true);
      fetchAvailableSkus('', true);
    }
  }, [activeTab]);

  // Reset and refetch SKUs when search changes
  useEffect(() => {
    if (activeTab === 'trends') {
      const timeoutId = setTimeout(() => {
        setSkuOffset(0);
        setHasMoreSkus(true);
        fetchAvailableSkus(skuSearch, true);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [skuSearch]);

  const handleSourceToggle = (source: string) => {
    setCurrentPage(1); // Reset to first page when changing source
    setSelectedSources(prev => {
      const isSelected = prev.includes(source);
      if (isSelected) {
        // Don't allow deselecting if it's the last source
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  const handleSelectAllSources = () => {
    setSelectedSources(availableSources);
  };

  const getSourceDisplayText = () => {
    if (selectedSources.length === 0) return 'Select sources';
    if (selectedSources.length === availableSources.length) return 'All Sources';
    if (selectedSources.length === 1) return selectedSources[0];
    return `${selectedSources.length} Sources`;
  };

  const handleSkuToggle = (sku: string) => {
    setSelectedSkus(prev => {
      const isSelected = prev.includes(sku);
      if (isSelected) {
        setSkuLimitWarning(false);
        return prev.filter(s => s !== sku);
      } else {
        // Check if we're already at the max limit
        if (prev.length >= MAX_SKUS_FOR_GRAPH) {
          setSkuLimitWarning(true);
          setTimeout(() => setSkuLimitWarning(false), 3000);
          return prev;
        }
        return [...prev, sku];
      }
    });
  };

  const truncateSku = (sku: string, maxLength: number = 35) => {
    if (sku.length <= maxLength) return sku;
    return sku.substring(0, maxLength) + '...';
  };

  const getSkuDisplayText = () => {
    if (selectedSkus.length === 0) return 'Select Product';
    if (selectedSkus.length === 1) return truncateSku(selectedSkus[0]);
    return `${selectedSkus.length}/${MAX_SKUS_FOR_GRAPH} Products`;
  };

  // Extract columns from data
  const columns = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter(key => key !== 'sourceBreakdown');
  }, [data]);

  // Filter columns for time-series view (date, SKU, and quantity)
  const filterColumns = React.useMemo(() => {
    if (activeTab === 'trends') {
      // For time-series, only allow filtering by date, SKU, and quantity
      return columns.filter(col => {
        const colLower = col.toLowerCase();
        return colLower.includes('date') || colLower.includes('quantity');
      });
    }
    return columns;
  }, [columns, activeTab]);

  // Filter handler functions
  const addFilter = (column: string, value: string, operation: FilterOperation) => {
    setActiveFilters(prev => {
      const existingIndex = prev.findIndex(
        f => f.column === column && f.value === value && f.operation === operation
      );
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      return [...prev, { column, value, operation }];
    });
  };

  const clearFilters = () => {
    setActiveFilters([]);
  };

  const handleEnableSnapshots = async () => {
    setEnablingSnapshots(true);
    try {
      // Step 1: Create inventory snapshot table
      const { error: tableError } = await invokeFunction(supabase,
        'create-inventory-snapshot-table',
        { body: { organisationId: organisation.id } }
      );

      if (tableError) throw tableError;

      // Step 2: Create financial snapshot table
      const { error: financialTableError } = await invokeFunction(supabase,
        'create-financial-snapshot-table-v2',
        { body: { organisationId: organisation.id } }
      );

      if (financialTableError) throw financialTableError;

      // Step 3: Update organisation record
      const { error: updateError } = await supabase
        .from('organisations')
        .update({
          inventory_snapshots_enabled: true,
          snapshot_table_created: true,
          financial_snapshot_table_created: true,
        })
        .eq('id', organisation.id);

      if (updateError) throw updateError;

      // Step 4: Setup cron schedule (one-time)
      // const { error: scheduleError } = await invokeFunction(supabase, 
      //   'setup-snapshot-schedule',
      //   { body: { organisationId: organisation.id } }
      // );

      // if (scheduleError) {
      //   console.warn('Schedule setup warning:', scheduleError);
      // }

      // Step 5: Take first inventory snapshot
      const { error: snapshotError } = await invokeFunction(supabase,
        'take-inventory-snapshot',
        { body: { organisationId: organisation.id } }
      );

      if (snapshotError) {
        console.warn('First inventory snapshot warning:', snapshotError);
      }

      // Step 6: Take first financial snapshot
      const { error: financialSnapshotError } = await invokeFunction(supabase,
        'take-financial-snapshot-v2',
        { body: { organisationId: organisation.id } }
      );

      if (financialSnapshotError) {
        console.warn('First financial snapshot warning:', financialSnapshotError);
      }

      toast.success('Inventory snapshots enabled! First snapshot is being created.');

      // Refresh snapshot status
      const { data: updatedOrg } = await supabase
        .from('organisations')
        .select('inventory_snapshots_enabled, last_snapshot_date')
        .eq('id', organisation.id)
        .single();

      if (updatedOrg) {
        setSnapshotsEnabled(updatedOrg.inventory_snapshots_enabled || false);
      }
    } catch (error) {
      console.error('Error enabling snapshots:', error);
      toast.error('Failed to enable snapshots. Please try again.');
    } finally {
      setEnablingSnapshots(false);
    }
  };

  const handleDisableSnapshots = async () => {
    if (!confirm('Disable inventory snapshots? Historical data will be preserved but no new snapshots will be taken.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('organisations')
        .update({ inventory_snapshots_enabled: false })
        .eq('id', organisation.id);

      if (error) throw error;

      toast.success('Inventory snapshots disabled');
      setSnapshotsEnabled(false);
    } catch (error) {
      console.error('Error disabling snapshots:', error);
      toast.error('Failed to disable snapshots');
    }
  };

  // Filter data by quantity and multi-column filters.
  const filteredData = React.useMemo(() => {
    let result = data;

    // Apply multi-column filters
    if (result && activeFilters.length > 0) {
      result = result.filter(item => {
        return activeFilters.every(filter => {
          const cellValue = item[filter.column];
          const filterValue = filter.value;

          // Handle blank checks
          if (filter.operation === 'is_blank') {
            return cellValue === null || cellValue === undefined || cellValue === '';
          }
          if (filter.operation === 'is_not_blank') {
            return cellValue !== null && cellValue !== undefined && cellValue !== '';
          }

          // Convert to strings for comparison
          const cellStr = String(cellValue || '').toLowerCase();
          const filterStr = String(filterValue || '').toLowerCase();

          // Numeric operations
          const cellNum = parseFloat(cellValue);
          const filterNum = parseFloat(filterValue);

          switch (filter.operation) {
            case 'equals':
              return !isNaN(cellNum) && !isNaN(filterNum)
                ? cellNum === filterNum
                : cellStr === filterStr;
            case 'contains':
              return cellStr.includes(filterStr);
            case 'startsWith':
              return cellStr.startsWith(filterStr);
            case 'endsWith':
              return cellStr.endsWith(filterStr);
            case 'gt':
              return !isNaN(cellNum) && !isNaN(filterNum) && cellNum > filterNum;
            case 'gte':
              return !isNaN(cellNum) && !isNaN(filterNum) && cellNum >= filterNum;
            case 'lt':
              return !isNaN(cellNum) && !isNaN(filterNum) && cellNum < filterNum;
            case 'lte':
              return !isNaN(cellNum) && !isNaN(filterNum) && cellNum <= filterNum;
            default:
              return true;
          }
        });
      });
    }

    return result;
  }, [data, activeFilters]);


  if (!hasConnections) {
    return (
      <div className="mt-6">
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <p>No data connections available for inventory reports yet.</p>
              <p className="text-sm mt-2">Upload inventory to get started.</p>
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setShowSnapshotUpload(true)}
                  className="text-sm"
                >
                  <UploadCloud className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {organisation?.id && (
          <ReceivedInventoryCsvUpload
            organisationId={organisation.id}

            open={showReceivedUpload}
            onClose={() => setShowReceivedUpload(false)}
            onUploadSuccess={handleInventoryUploadSuccess}
          />
        )}

        {organisation?.id && (
          <SnapshotCsvUpload
            organisationId={organisation.id}

            open={showSnapshotUpload}
            onClose={() => setShowSnapshotUpload(false)}
            onUploadSuccess={handleInventoryUploadSuccess}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="space-y-4">
        {/* Snapshot Control Card */}
        {/* <Card className="mb-6 border-primary/20 bg-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Inventory Snapshots
                </CardTitle>
                <CardDescription>
                  {snapshotsEnabled 
                    ? 'Track inventory trends over time with automated daily snapshots'
                    : 'Enable to track inventory trends over time'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                {snapshotsEnabled && (
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                    Enabled
                  </Badge>
                )}
                <Button
                  variant={snapshotsEnabled ? "outline" : "default"}
                  size="sm"
                  onClick={snapshotsEnabled ? handleDisableSnapshots : handleEnableSnapshots}
                  disabled={enablingSnapshots}
                >
                  {enablingSnapshots && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {snapshotsEnabled ? 'Disable' : 'Enable Snapshots'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card> */}

        <Tabs defaultValue="table" className="w-full" value={activeTab} onValueChange={(v) => setActiveTab(v as 'table' | 'trends' | 'financing' | 'whatif' | 'adjust' | 'audit')}>
          <div className="flex items-center justify-between mb-2">
            <TabsList className="w-auto">
              <TabsTrigger value="table">Current Stock</TabsTrigger>
              {featureFlags.showInventoryTrends && !isTestClone && (
                <TabsTrigger value="trends" disabled={!snapshotsEnabled}>
                  Trends Over Time
                </TabsTrigger>
              )}
              {/* {featureFlags.showInventoryFinancingTapes && !isTestClone && (
                <TabsTrigger value="financing" disabled={!snapshotsEnabled}>
                  Financing Tapes
                </TabsTrigger>
              )}
              {featureFlags.showInventoryWhatIf && !isTestClone && (
                <TabsTrigger value="whatif" disabled={!snapshotsEnabled}>
                  What-If Analysis
                </TabsTrigger>
              )} */}
              {featureFlags.showInventoryAdjust && (
                <TabsTrigger value="adjust">Adjust</TabsTrigger>
              )}
              {featureFlags.showInventoryAdjust && (
                <TabsTrigger value="audit">Audit log</TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Filter Controls - only for table and trends tabs */}
          {activeTab !== 'financing' && activeTab !== 'whatif' && activeTab !== 'adjust' && activeTab !== 'audit' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">Inventory Data</h3>
                </div>

                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4 mr-2" />
                        Source Settings
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <InventorySourceSettings
                        organisation={organisation}
                        onUpdate={() => {
                          // Refresh organisation data first to get new config
                          onOrganisationUpdate?.();
                          // Then refetch inventory with new config
                          refetch();
                        }}
                      />
                    </PopoverContent>
                  </Popover>

                  {featureFlags.showInventoryAdjust && (
                    <Button
                      variant="outline"
                      onClick={() => setShowImportTypeDialog(true)}
                      className="text-sm"
                    >
                      <UploadCloud className="h-4 w-4 mr-2" />
                      Upload
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => setShowExportManager(true)}
                    className="text-sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Manager
                  </Button>
                </div>
              </div>

              {/* Filter Controls - matching reports tab layout */}
              <div className="flex items-center gap-4 flex-wrap">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[200px] justify-between">
                      {getSourceDisplayText()}
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0" align="start">
                    <div className="p-2">
                      <div className="flex items-center justify-between px-2 py-1.5 mb-1">
                        <span className="text-sm font-medium">Data Sources</span>
                        {selectedSources.length !== availableSources.length && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0 text-xs text-primary hover:text-primary"
                            onClick={handleSelectAllSources}
                          >
                            Select All
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {availableSources.map((source) => {
                          const isOnlySelected = selectedSources.length === 1 && selectedSources.includes(source);
                          return (
                            <div
                              key={source}
                              className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded-sm cursor-pointer"
                            >
                              <Checkbox
                                id={`source-${source}`}
                                checked={selectedSources.includes(source)}
                                disabled={isOnlySelected}
                                onCheckedChange={() => handleSourceToggle(source)}
                              />
                              <label
                                htmlFor={`source-${source}`}
                                className={`text-sm flex-1 ${isOnlySelected ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                              >
                                {source}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* SKU Filter for Trends View */}
                {activeTab === 'trends' && (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[280px] justify-between"
                        >
                          <span className="truncate">{getSkuDisplayText()}</span>
                          <ChevronDown className="ml-2 h-4 w-4 opacity-50 flex-shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0 bg-background z-50" align="start">
                        {skuLimitWarning && (
                          <div className="bg-yellow-50 border-b border-yellow-200 px-3 py-2 text-xs text-yellow-800">
                            Maximum of {MAX_SKUS_FOR_GRAPH} Products can be selected for graph view
                          </div>
                        )}
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search Products..."
                            value={skuSearch}
                            onValueChange={setSkuSearch}
                          />
                          <CommandList
                            onScroll={(e: React.UIEvent<HTMLDivElement>) => {
                              const target = e.currentTarget;
                              const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

                              if (scrolledToBottom && hasMoreSkus && !loadingSkus) {
                                fetchAvailableSkus(skuSearch, false);
                              }
                            }}
                          >
                            <CommandEmpty>
                              {loadingSkus ? 'Loading Products...' : 'No Products found.'}
                            </CommandEmpty>

                            {/* Selected SKUs at the top */}
                            {selectedSkus.length > 0 && (
                              <>
                                <CommandGroup heading="Selected">
                                  {selectedSkus.map((sku) => {
                                    return (
                                      <CommandItem
                                        key={`selected-${sku}`}
                                        value={sku}
                                        onSelect={() => handleSkuToggle(sku)}
                                        className="cursor-pointer bg-accent/30"
                                      >
                                        <Checkbox
                                          checked={true}
                                          className="mr-2"
                                        />
                                        <span className={`truncate`} title={sku}>
                                          {sku}
                                        </span>
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                                <div className="border-t" />
                              </>
                            )}

                            {/* Available (unselected) SKUs */}
                            <CommandGroup heading={selectedSkus.length > 0 ? "Available" : undefined}>
                              {availableSkus
                                .filter(sku => !selectedSkus.includes(sku))
                                .map((sku) => {
                                  return (
                                    <CommandItem
                                      key={sku}
                                      value={sku}
                                      onSelect={() => handleSkuToggle(sku)}
                                      className="cursor-pointer"
                                    >
                                      <Checkbox
                                        checked={false}
                                        className="mr-2"
                                      />
                                      <span className="truncate" title={sku}>
                                        {sku}
                                      </span>
                                    </CommandItem>
                                  );
                                })}
                              {loadingSkus && (
                                <div className="text-sm text-center py-2 text-muted-foreground">
                                  Loading more...
                                </div>
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {selectedSkus.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {selectedSkus.length}/{MAX_SKUS_FOR_GRAPH} Products selected for graph
                      </div>
                    )}
                  </>
                )}

                {/* Date controls - same for both tabs */}
                <>
                  <Input
                    type="date"
                    value={format(customStartDate, 'yyyy-MM-dd')}
                    onChange={(e) => setCustomStartDate(new Date(e.target.value))}
                    className="w-[160px]"
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="date"
                    value={format(customEndDate, 'yyyy-MM-dd')}
                    onChange={(e) => setCustomEndDate(new Date(e.target.value))}
                    className="w-[160px]"
                  />
                </>

                <MultiColumnFilter
                  columns={filterColumns}
                  activeFilters={activeFilters}
                  onAddFilter={addFilter}
                  onClearAllFilters={clearFilters}
                  tableData={data || []}
                />
              </div>

              {/* Page Size Control - only for table view */}
              {activeTab === 'table' && (
                <div className="flex items-center justify-between mb-4 mt-6">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>Page size:</span>
                    <Select value={rowsPerPage.toString()} onValueChange={(value) => {
                      setRowsPerPage(Number(value));
                      setCurrentPage(1);
                    }}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="250">250</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </>
          )}

          <TabsContent value="table">
            {error ? (
              <div className="border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-destructive mb-4">Error loading inventory data: {error}</p>
                <Button onClick={() => refetch()} variant="outline">
                  Retry
                </Button>
              </div>
            ) : isLoading ? (
              <div className="border border-gray-200 rounded-lg p-8 text-center text-muted-foreground">
                Loading inventory data...
              </div>
            ) : filteredData && filteredData.length > 0 ? (
              <InventoryDataTable
                data={filteredData}
                isLoading={isLoading}
                totalCount={totalCount}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={(newSize) => {
                  setRowsPerPage(newSize);
                  setCurrentPage(1);
                }}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSortChange={(column, direction) => {
                  setSortColumn(column);
                  setSortDirection(direction);
                }}
              />
            ) : (
              <div className="border border-gray-200 rounded-lg p-8 text-center text-muted-foreground">
                No inventory data available for the selected filters.
              </div>
            )}
          </TabsContent>

          {/* Trends Tab - only show if snapshots enabled */}
          {snapshotsEnabled && (
            <TabsContent value="trends">
              <InventoryTimeSeriesView
                data={filteredData}
                fullData={fullData}
                isLoading={isLoading}
                error={error}
                totalCount={totalCount}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={(newSize) => {
                  setRowsPerPage(newSize);
                  setCurrentPage(1);
                }}
                onViewModeChange={setTrendsViewMode}
                selectedSkus={selectedSkus}
              />
            </TabsContent>
          )}

          {/* Financing Tapes Tab - only show if snapshots enabled */}
          {snapshotsEnabled && (
            <TabsContent value="financing">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        Financing Tapes
                      </CardTitle>
                      <CardDescription>
                        Lock inventory snapshots for financing purposes
                      </CardDescription>
                    </div>

                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button disabled={!financingTablesCreated || creatingFinancingTable}>
                          <Plus className="h-4 w-4 mr-2" />
                          Lock Current Snapshot
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Lock Financing Tape</DialogTitle>
                          <DialogDescription>
                            Create a locked snapshot of your inventory for financing purposes
                          </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="lockDate">Lock Date *</Label>
                            <Input
                              id="lockDate"
                              type="date"
                              value={lockDate}
                              onChange={(e) => setLockDate(e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="tapeName">Tape Name</Label>
                            <Input
                              id="tapeName"
                              placeholder="Optional custom name"
                              value={tapeName}
                              onChange={(e) => setTapeName(e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Textarea
                              id="notes"
                              placeholder="Optional notes about this tape"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              id="recurring"
                              checked={isRecurring}
                              onCheckedChange={setIsRecurring}
                            />
                            <Label htmlFor="recurring">Set as recurring funding cycle</Label>
                          </div>

                          {isRecurring && (
                            <div className="space-y-2">
                              <Label htmlFor="fundingCycleDay">Funding Cycle Day (1-31)</Label>
                              <Input
                                id="fundingCycleDay"
                                type="number"
                                min="1"
                                max="31"
                                value={fundingCycleDay || ""}
                                onChange={(e) => setFundingCycleDay(parseInt(e.target.value))}
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button
                            onClick={handleLockTape}
                            disabled={lockTapeMutation.isPending}
                          >
                            {lockTapeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Lock Snapshot
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>

                <CardContent>
                  {!financingTablesCreated ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">
                        Financing tapes table not created yet. Click below to create it.
                      </p>
                      <Button
                        onClick={createFinancingTapesTable}
                        disabled={creatingFinancingTable}
                      >
                        {creatingFinancingTable && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create Financing Tapes Table
                      </Button>
                    </div>
                  ) : isLoadingTapes ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Loading financing tapes...
                    </div>
                  ) : tapes && tapes.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tape Name</TableHead>
                          <TableHead>Lock Date</TableHead>
                          <TableHead>SKUs</TableHead>
                          <TableHead>Total Units</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tapes.map((tape: any) => (
                          <TableRow key={tape.tape_id}>
                            <TableCell className="font-medium">{tape.tape_name}</TableCell>
                            <TableCell>{format(new Date(tape.lock_date), 'PP')}</TableCell>
                            <TableCell>{tape.sku_count?.toLocaleString()}</TableCell>
                            <TableCell>{tape.total_inventory_units?.toLocaleString()}</TableCell>
                            <TableCell>
                              <Badge variant={tape.status === 'active' ? 'default' : 'secondary'}>
                                {tape.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleExportTape(tape.tape_id, tape.tape_name)}
                                  disabled={exportingTapeId === tape.tape_id}
                                  title={exportingTapeId === tape.tape_id ? "Exporting..." : "Export tape"}
                                >
                                  {exportingTapeId === tape.tape_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => archiveTapeMutation.mutate(tape.tape_id)}
                                  disabled={tape.status === 'archived'}
                                  title="Archive tape"
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No financing tapes created yet. Click "Lock Current Snapshot" to create one.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* What-If Analysis Tab - only show if snapshots enabled */}
          {snapshotsEnabled && (
            <TabsContent value="whatif">
              <WhatIfInventoryAnalysis organisation={organisation} />
            </TabsContent>
          )}


          {featureFlags.showInventoryAdjust && (
            <TabsContent value="adjust">
              {adjustTabLedgerSummary.hybrid && (
                <Alert className="mb-4 border-amber-200 bg-amber-50">
                  <AlertDescription className="text-sm text-amber-950">
                    <strong>Hybrid inventory sources.</strong> This organisation has both live and CSV/manual ledger
                    sources—pick the correct <strong>source</strong> on each adjustment. Live-source adjustments are{' '}
                    <strong>transient</strong> (dropped after the next live inventory sync completes).
                  </AlertDescription>
                </Alert>
              )}
              {adjustTabLedgerSummary.liveOnly && (
                <Alert className="mb-4">
                  <AlertDescription className="text-sm">
                    Only <strong>live</strong> ledger sources are registered. Manual adjustments here are transient until
                    the next inventory sync. Sales deductions from CSV reconciliation are not applied to live sources.
                  </AlertDescription>
                </Alert>
              )}
              {organisation?.id && <ManualAdjustmentForm organisationId={organisation.id} onSuccess={refetch} />}
            </TabsContent>
          )}
          {featureFlags.showInventoryAdjust && (
            <TabsContent value="audit">
              {organisation?.id && <InventoryAuditLogTable organisationId={organisation.id} />}
            </TabsContent>
          )}

        </Tabs>
      </div>

      <ImportTypeDialog
        open={showImportTypeDialog}
        onClose={() => setShowImportTypeDialog(false)}
        onContinue={(type: ImportType) => {
          setShowImportTypeDialog(false);
          if (type === "received") {
            setShowReceivedUpload(true);
          } else {
            setShowSnapshotUpload(true);
          }
        }}
      />

      {organisation?.id && (
        <ReceivedInventoryCsvUpload
          organisationId={organisation.id}
          open={showReceivedUpload}
          onClose={() => setShowReceivedUpload(false)}
          onUploadSuccess={handleInventoryUploadSuccess}
        />
      )}

      {organisation?.id && (
        <SnapshotCsvUpload
          organisationId={organisation.id}
          open={showSnapshotUpload}
          onClose={() => setShowSnapshotUpload(false)}
          onUploadSuccess={handleInventoryUploadSuccess}
        />
      )}

      {showExportManager && (activeTab === 'table' || activeTab === 'trends') && (
        <UnifiedExportManager
          organisation={organisation}
          isOpen={showExportManager}
          onClose={() => setShowExportManager(false)}
          context="inventory"
          inventoryActiveTab={activeTab}
          inventoryFilters={{
            sources: selectedSources,
            dateRange,
            customStartDate,
            customEndDate,
            selectedSkus: activeTab === 'trends' ? selectedSkus : undefined,
            sortColumn,
            sortDirection,
            multiColumnFilters: activeFilters
          }}
        />
      )}
    </div>
  );
};

export default OrganisationInventoryTab;